import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import { configApi } from "../api/config"
import { uploadApi } from "../api/upload"
import { DEFAULT_TEMPLATE_KEY, getTemplateConfig } from "../data/templates"

function revokeAllPendingBannerUrls(pendingBannerUploads) {
  if (!pendingBannerUploads || typeof pendingBannerUploads !== "object") return

  Object.values(pendingBannerUploads).forEach((entry) => {
    if (entry?.previewUrl && typeof entry.previewUrl === "string") {
      try {
        URL.revokeObjectURL(entry.previewUrl)
      } catch (error) {
        console.warn("Failed to revoke blob URL:", error)
      }
    }
  })
}

function revokeAllPendingIconUrls(pendingIconUploads) {
  if (!pendingIconUploads || typeof pendingIconUploads !== "object") return

  Object.values(pendingIconUploads).forEach((entry) => {
    if (entry?.previewUrl && typeof entry.previewUrl === "string") {
      try {
        URL.revokeObjectURL(entry.previewUrl)
      } catch (error) {
        console.warn("Failed to revoke icon blob URL:", error)
      }
    }
  })
}

function isPendingIconMarker(value) {
  return typeof value === "string" && value.startsWith("pending-icon://")
}

function replacePendingIconMarkers(config, uploadResultsByMarker) {
  if (!config || !uploadResultsByMarker || Object.keys(uploadResultsByMarker).length === 0) return

  function visit(node) {
    if (!node || typeof node !== "object") return

    if (Array.isArray(node)) {
      node.forEach(visit)
      return
    }

    Object.entries(node).forEach(([key, value]) => {
      if (typeof value === "string" && isPendingIconMarker(value) && uploadResultsByMarker[value]) {
        node[key] = uploadResultsByMarker[value]
        return
      }
      if (value && typeof value === "object") {
        visit(value)
      }
    })
  }

  visit(config)
}

function applyUploadedBannerUrls(config, uploadResults) {
  if (!config?.pages) return

  config.pages.forEach((page) => {
    if (!page.components) return

    page.components.forEach((component) => {
      if (component.settings?.banners && Array.isArray(component.settings.banners)) {
        component.settings.banners.forEach((banner) => {
          if (uploadResults[banner.id]) {
            banner.imageUrl = uploadResults[banner.id]
          }
        })
      }

      if (component.type === "middleBanner" && component.settings) {
        const desktopKey = `${component.id}-desktop`
        const mobileKey = `${component.id}-mobile`

        if (uploadResults[desktopKey]) {
          component.settings.desktopImageUrl = uploadResults[desktopKey]
        }

        if (uploadResults[mobileKey]) {
          component.settings.mobileImageUrl = uploadResults[mobileKey]
        }
      }
    })
  })
}

async function uploadPendingAssets(pendingBannerUploads, pendingIconUploads) {
  const uploadResults = {}
  const iconUploadResultsByMarker = {}

  const bannerUploadPromises = Object.entries(pendingBannerUploads).map(async ([bannerId, entry]) => {
    try {
      const result = await uploadApi.uploadBannerImage(entry.file)
      uploadResults[bannerId] = result.imageUrl
      return { bannerId, success: true, imageUrl: result.imageUrl }
    } catch (error) {
      console.error(`Не удалось загрузить файл для баннера ${bannerId}:`, error)
      return { bannerId, success: false, error: error.message }
    }
  })

  const iconUploadPromises = Object.entries(pendingIconUploads).map(async ([ownerKey, entry]) => {
    try {
      const result = await uploadApi.uploadIconFile(entry.file)
      if (entry.marker) {
        iconUploadResultsByMarker[entry.marker] = result.imageUrl
      }
      return { ownerKey, success: true, imageUrl: result.imageUrl }
    } catch (error) {
      console.error(`Не удалось загрузить файл иконки ${ownerKey}:`, error)
      return { ownerKey, success: false, error: error.message }
    }
  })

  const uploadSettled = await Promise.allSettled([...bannerUploadPromises, ...iconUploadPromises])
  return { uploadSettled, uploadResults, iconUploadResultsByMarker }
}

function collectFailedUploads(uploadSettled) {
  return uploadSettled
    .filter((result) => result.status === "rejected" || (result.status === "fulfilled" && !result.value.success))
    .map((result) => {
      if (result.status === "rejected") {
        return result.reason?.message || String(result.reason) || "Неизвестная ошибка"
      }
      return result.value.bannerId || result.value.ownerKey || "Неизвестный ассет"
    })
}

async function processPendingUploads({ baseConfig, pendingBannerUploads, pendingIconUploads }) {
  const { uploadSettled, uploadResults, iconUploadResultsByMarker } = await uploadPendingAssets(pendingBannerUploads, pendingIconUploads)
  const failedUploads = collectFailedUploads(uploadSettled)

  if (failedUploads.length > 0) {
    return {
      updatedConfig: null,
      errorMessage: `Не удалось загрузить файлы для баннеров: ${failedUploads.join(", ")}`,
    }
  }

  const updatedConfig = JSON.parse(JSON.stringify(baseConfig))
  replacePendingIconMarkers(updatedConfig, iconUploadResultsByMarker)
  applyUploadedBannerUrls(updatedConfig, uploadResults)

  return { updatedConfig, errorMessage: null }
}

function resetPendingUploadsState(state) {
  revokeAllPendingBannerUrls(state.pendingBannerUploads)
  state.pendingBannerUploads = {}
  revokeAllPendingIconUrls(state.pendingIconUploads)
  state.pendingIconUploads = {}
}

function cloneConfigData(value) {
  if (value == null || typeof value !== "object") return value

  if (typeof structuredClone === "function") {
    return structuredClone(value)
  }

  return JSON.parse(JSON.stringify(value))
}

const initialState = {
  activeEnv: "prod",
  draft: null,
  templateKey: DEFAULT_TEMPLATE_KEY,
  selectedPageId: null,
  selectedComponentId: null,
  selectedGlobalItemId: null,
  isDirty: false,
  isFetchingDraft: false,
  isSaving: false,
  isPublishing: false,
  lastSaved: null,
  error: null,
  draftContentHash: null,
  publishedContentHash: null,
  deletedBannerImages: [],
  pendingBannerUploads: {}, // { [bannerId]: File }
  pendingIconUploads: {}, // { [ownerKey]: { file, previewUrl, marker } }
}

/**
 * Migrates component names from legacy Russian strings to i18n keys (<template>.components.<type>).
 * Called when loading a draft so old drafts display correctly when switching language.
 * @param {object} draft - Draft config (mutated in place)
 */
function migrateComponentNames(draft, templateKey = DEFAULT_TEMPLATE_KEY) {
  if (!draft?.pages || !Array.isArray(draft.pages)) return
  const normalizedTemplateKey = templateKey || DEFAULT_TEMPLATE_KEY
  for (const page of draft.pages) {
    if (!page?.components || !Array.isArray(page.components)) continue
    for (const component of page.components) {
      if (
        component.name != null &&
        typeof component.name === "string" &&
        !component.name.includes(".components.")
      ) {
        component.name = `${normalizedTemplateKey}.components.${component.type || "unknown"}`
      }
    }
  }
}

/**
 * Migrates localized text fields from plain strings to object form { en: string }.
 * This keeps old drafts compatible with the new multi-language format.
 * @param {object} draft - Draft config (mutated in place)
 */
function migrateLocalizedFields(draft) {
  if (!draft) return

  const toLocalizedObject = (value) => {
    if (!value || typeof value !== "string") return value
    return { en: value }
  }

  // Component settings on pages
  if (Array.isArray(draft.pages)) {
    for (const page of draft.pages) {
      if (!page?.components || !Array.isArray(page.components)) continue
      for (const component of page.components) {
        const type = component?.type
        const settings = component?.settings
        if (!settings || typeof settings !== "object") continue

        // Banners: each banner has title/buttonText
        if (type === "banners" && Array.isArray(settings.banners)) {
          settings.banners.forEach((banner) => {
            if (!banner || typeof banner !== "object") return
            if (typeof banner.title === "string") banner.title = toLocalizedObject(banner.title)
            if (typeof banner.buttonText === "string") banner.buttonText = toLocalizedObject(banner.buttonText)
          })
        }

        // middleBanner: title/buttonText
        if (type === "middleBanner" && settings.banner) {
          const banner = settings.banner
          if (typeof banner.title === "string") banner.title = toLocalizedObject(banner.title)
          if (typeof banner.buttonText === "string") banner.buttonText = toLocalizedObject(banner.buttonText)
        }

        // gamesRow: customTitle
        if (type === "gamesRow") {
          if (typeof settings.customTitle === "string") {
            settings.customTitle = toLocalizedObject(settings.customTitle)
          }
        }

        // slotsSwiper: title/ctaLabel
        if (type === "slotsSwiper") {
          if (typeof settings.title === "string") {
            settings.title = toLocalizedObject(settings.title)
          }
          if (typeof settings.ctaLabel === "string") {
            settings.ctaLabel = toLocalizedObject(settings.ctaLabel)
          }
        }

        // highlightGame: displayText
        if (type === "highlightGame") {
          if (typeof settings.displayText === "string") {
            settings.displayText = toLocalizedObject(settings.displayText)
          }
        }

        // profileButton: title
        if (type === "profileButton") {
          if (typeof settings.title === "string") {
            settings.title = toLocalizedObject(settings.title)
          }
        }

        // textBlock, promotion, bonus: title/text/buttonText
        if (type === "textBlock" || type === "promotion" || type === "bonus") {
          if (typeof settings.title === "string") {
            settings.title = toLocalizedObject(settings.title)
          }
          if (typeof settings.text === "string") {
            settings.text = toLocalizedObject(settings.text)
          }
          if (type === "bonus" && typeof settings.buttonText === "string") {
            settings.buttonText = toLocalizedObject(settings.buttonText)
          }
        }
      }
    }
  }

  // Global footer settings: column titles and item titles
  const footerSettings = draft.globalSettings?.footer?.settings
  if (footerSettings && typeof footerSettings === "object") {
    if (Array.isArray(footerSettings.categories)) {
      footerSettings.categories.forEach((category) => {
        if (!category || typeof category !== "object") return
        if (typeof category.name === "string") {
          category.name = toLocalizedObject(category.name)
        }
      })
    }

    const normalizeColumn = (columnKey) => {
      const column = footerSettings[columnKey]
      if (!column || typeof column !== "object") return

      if (typeof column.title === "string") {
        column.title = toLocalizedObject(column.title)
      }

      if (Array.isArray(column.items)) {
        column.items.forEach((item) => {
          if (!item || typeof item !== "object") return
          if (typeof item.title === "string") {
            item.title = toLocalizedObject(item.title)
          }
        })
      }
    }

    normalizeColumn("column1")
    normalizeColumn("column2")
  }
}

/**
 * Migrates middleBanner structure from old nested banner object to flat settings.
 * Old: settings.banner = { desktopImageUrl, mobileImageUrl, title, buttonText, buttonUrl }
 * New: settings = { desktopImageUrl, mobileImageUrl, title, buttonText, buttonUrl }
 * @param {object} draft - Draft config (mutated in place)
 */
function migrateMiddleBannerStructure(draft) {
  if (!draft?.pages || !Array.isArray(draft.pages)) return

  for (const page of draft.pages) {
    if (!page?.components || !Array.isArray(page.components)) continue
    for (const component of page.components) {
      if (component.type === "middleBanner" && component.settings?.banner) {
        const banner = component.settings.banner

        // Переносим поля из banner напрямую в settings
        if (banner.desktopImageUrl !== undefined) {
          component.settings.desktopImageUrl = banner.desktopImageUrl
        }
        if (banner.mobileImageUrl !== undefined) {
          component.settings.mobileImageUrl = banner.mobileImageUrl
        }
        if (banner.title !== undefined) {
          component.settings.title = banner.title
        }
        if (banner.buttonText !== undefined) {
          component.settings.buttonText = banner.buttonText
        }
        if (banner.buttonUrl !== undefined) {
          component.settings.buttonUrl = banner.buttonUrl
        }

        // Удаляем старую структуру banner
        delete component.settings.banner
      }
    }
  }
}

export const fetchDraft = createAsyncThunk("builder/fetchDraft", async ({ siteId, env }, { rejectWithValue, getState }) => {
  try {
    const response = await configApi.getDraft(siteId, env)
    const templateKey = getState()?.sites?.currentSite?.templateKey ?? DEFAULT_TEMPLATE_KEY
    return { ...response.data, templateKey, env }
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || "Ошибка загрузки конфигурации")
  }
})

export const saveDraft = createAsyncThunk("builder/saveDraft", async ({ siteId, config }, { rejectWithValue, getState }) => {
  try {
    const state = getState()
    const env = state.builder.activeEnv ?? "prod"
    const deletedBannerImages = state.builder.deletedBannerImages || []
    const pendingBannerUploads = state.builder.pendingBannerUploads || {}
    const pendingIconUploads = state.builder.pendingIconUploads || {}
    const { updatedConfig, errorMessage } = await processPendingUploads({
      baseConfig: config,
      pendingBannerUploads,
      pendingIconUploads,
    })

    if (errorMessage) {
      return rejectWithValue(errorMessage)
    }

    // Удаляем все файлы с сервера параллельно
    if (deletedBannerImages.length > 0) {
      const deletePromises = deletedBannerImages.map((filename) =>
        uploadApi.deleteBannerImage(filename).catch((error) => {
          // Логируем ошибки, но не блокируем сохранение
          console.warn(`Не удалось удалить файл ${filename} с сервера:`, error)
          return { filename, error: error.message }
        }),
      )

      await Promise.allSettled(deletePromises)
    }

    // Отправляем обновленный конфиг на сервер
    const response = await configApi.saveDraft(siteId, updatedConfig, env)
    return response.data
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || error.message || "Ошибка сохранения")
  }
})

export const publishConfig = createAsyncThunk("builder/publishConfig", async ({ siteId, notes }, { rejectWithValue, getState }) => {
  try {
    const state = getState()
    const env = state.builder.activeEnv ?? "prod"
    const draft = state.builder.draft
    const pendingBannerUploads = state.builder.pendingBannerUploads || {}
    const pendingIconUploads = state.builder.pendingIconUploads || {}

    // Обрабатываем pending uploads перед публикацией (аналогично saveDraft)
    if (Object.keys(pendingBannerUploads).length > 0 || Object.keys(pendingIconUploads).length > 0) {
      const { updatedConfig, errorMessage } = await processPendingUploads({
        baseConfig: draft,
        pendingBannerUploads,
        pendingIconUploads,
      })

      if (errorMessage) {
        return rejectWithValue(errorMessage)
      }

      // Сохраняем draft с обновленными imageUrl перед публикацией
      await configApi.saveDraft(siteId, updatedConfig, env)
    }

    // Публикуем конфиг
    const response = await configApi.publish(siteId, notes, env)
    console.log(response.data)

    // Получаем обновленный draft после публикации
    const draftResponse = await configApi.getDraft(siteId, env)

    return {
      ...response.data,
      draft: draftResponse.data.draft, // Добавляем draft в payload
    }
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || error.message || "Ошибка публикации")
  }
})

const builderSlice = createSlice({
  name: "builder",
  initialState,
  reducers: {
    selectPage: (state, action) => {
      state.selectedPageId = action.payload
      state.selectedComponentId = null
      state.selectedGlobalItemId = null
    },
    selectComponent: (state, action) => {
      state.selectedComponentId = action.payload
    },
    selectGlobalItem: (state, action) => {
      state.selectedGlobalItemId = action.payload
      state.selectedPageId = null
      state.selectedComponentId = null
    },
    setActiveEnv: (state, action) => {
      const nextEnv = action.payload
      if (state.activeEnv === nextEnv) return

      state.activeEnv = nextEnv
      state.isDirty = false
      state.error = null

      state.draft = null
      state.templateKey = DEFAULT_TEMPLATE_KEY
      state.selectedPageId = null
      state.selectedComponentId = null
      state.selectedGlobalItemId = null

      state.draftContentHash = null
      state.publishedContentHash = null
      state.lastSaved = null
      state.deletedBannerImages = []

      state.isSaving = false
      state.isPublishing = false

      resetPendingUploadsState(state)
    },
    updateGlobalItemSettings: (state, action) => {
      if (!state.draft) return
      const { key, settings } = action.payload
      if (!state.draft.globalSettings) {
        const templateKey = state.templateKey ?? DEFAULT_TEMPLATE_KEY
        state.draft.globalSettings = cloneConfigData(getTemplateConfig(templateKey).defaultGlobalSettings)
      }
      if (!state.draft.globalSettings[key]) {
        state.draft.globalSettings[key] = { settings: {} }
      }
      state.draft.globalSettings[key].settings = {
        ...state.draft.globalSettings[key].settings,
        ...settings,
      }
      state.isDirty = true
    },
    updatePage: (state, action) => {
      if (!state.draft) return
      const { pageId, updates } = action.payload
      const pageIndex = state.draft.pages.findIndex((p) => p.id === pageId)
      if (pageIndex !== -1) {
        state.draft.pages[pageIndex] = { ...state.draft.pages[pageIndex], ...updates }
        state.isDirty = true
      }
    },
    updateComponent: (state, action) => {
      if (!state.draft) return
      const { pageId, componentId, updates } = action.payload
      const page = state.draft.pages.find((p) => p.id === pageId)
      if (page) {
        const componentIndex = page.components.findIndex((c) => c.id === componentId)
        if (componentIndex !== -1) {
          page.components[componentIndex] = { ...page.components[componentIndex], ...updates }
          state.isDirty = true
        }
      }
    },
    updateComponentSettings: (state, action) => {
      if (!state.draft) return
      const { pageId, componentId, settings } = action.payload
      const page = state.draft.pages.find((p) => p.id === pageId)
      if (page) {
        const component = page.components.find((c) => c.id === componentId)
        if (component) {
          component.settings = { ...component.settings, ...settings }
          state.isDirty = true
        }
      }
    },
    addPage: (state, action) => {
      if (!state.draft) return
      state.draft.pages.push(action.payload)
      state.isDirty = true
    },
    removePage: (state, action) => {
      if (!state.draft) return
      state.draft.pages = state.draft.pages.filter((p) => p.id !== action.payload)
      if (state.selectedPageId === action.payload) {
        state.selectedPageId = state.draft.pages[0]?.id || null
      }
      state.isDirty = true
    },
    addComponent: (state, action) => {
      if (!state.draft) return
      const { pageId, component } = action.payload
      const page = state.draft.pages.find((p) => p.id === pageId)
      if (page) {
        page.components.push(component)
        state.isDirty = true
      }
    },
    removeComponent: (state, action) => {
      if (!state.draft) return
      const { pageId, componentId } = action.payload
      const page = state.draft.pages.find((p) => p.id === pageId)
      if (page) {
        page.components = page.components.filter((c) => c.id !== componentId)
        if (state.selectedComponentId === componentId) {
          state.selectedComponentId = null
        }
        const componentOwnerPrefix = `${componentId}:`
        Object.keys(state.pendingIconUploads).forEach((ownerKey) => {
          if (!ownerKey.startsWith(componentOwnerPrefix)) return
          const previewUrl = state.pendingIconUploads[ownerKey]?.previewUrl
          if (previewUrl) {
            try {
              URL.revokeObjectURL(previewUrl)
            } catch (error) {
              console.warn("Failed to revoke icon blob URL while removing component:", error)
            }
          }
          delete state.pendingIconUploads[ownerKey]
        })
        state.isDirty = true
      }
    },
    reorderComponents: (state, action) => {
      if (!state.draft) return
      const { pageId, componentIds } = action.payload
      const page = state.draft.pages.find((p) => p.id === pageId)
      if (page) {
        const componentsById = new Map(page.components.map((component) => [component.id, component]))
        const reordered = componentIds.map((id) => componentsById.get(id)).filter(Boolean)
        page.components = reordered
        state.isDirty = true
      }
    },
    clearBuilder: (state) => {
      return initialState
    },
    clearBuilderError: (state) => {
      state.error = null
    },
    addDeletedBannerImage: (state, action) => {
      const filename = action.payload
      if (filename && !state.deletedBannerImages.includes(filename)) {
        state.deletedBannerImages.push(filename)
      }
    },
    clearDeletedBannerImages: (state) => {
      state.deletedBannerImages = []
    },
    addPendingBannerUpload: (state, action) => {
      const { bannerId, file, previewUrl } = action.payload
      if (bannerId && file && previewUrl) {
        // Если уже есть pending upload для этого баннера, очищаем старый blob URL
        if (state.pendingBannerUploads[bannerId]?.previewUrl) {
          try {
            URL.revokeObjectURL(state.pendingBannerUploads[bannerId].previewUrl)
          } catch (error) {
            console.warn("Failed to revoke old blob URL:", error)
          }
        }
        state.pendingBannerUploads[bannerId] = { file, previewUrl }
        state.isDirty = true
      }
    },
    removePendingBannerUpload: (state, action) => {
      const bannerId = action.payload
      if (bannerId && state.pendingBannerUploads[bannerId]) {
        // Очищаем blob URL перед удалением
        if (state.pendingBannerUploads[bannerId].previewUrl) {
          try {
            URL.revokeObjectURL(state.pendingBannerUploads[bannerId].previewUrl)
          } catch (error) {
            console.warn("Failed to revoke blob URL:", error)
          }
        }
        delete state.pendingBannerUploads[bannerId]
      }
    },
    clearPendingBannerUploads: (state) => {
      // Очищаем все blob URLs перед сбросом
      revokeAllPendingBannerUrls(state.pendingBannerUploads)
      state.pendingBannerUploads = {}
    },
    addPendingIconUpload: (state, action) => {
      const { ownerKey, file, previewUrl, marker } = action.payload
      if (ownerKey && file && previewUrl && marker) {
        if (state.pendingIconUploads[ownerKey]?.previewUrl) {
          try {
            URL.revokeObjectURL(state.pendingIconUploads[ownerKey].previewUrl)
          } catch (error) {
            console.warn("Failed to revoke old icon blob URL:", error)
          }
        }
        state.pendingIconUploads[ownerKey] = { file, previewUrl, marker }
        state.isDirty = true
      }
    },
    removePendingIconUpload: (state, action) => {
      const ownerKey = action.payload
      if (ownerKey && state.pendingIconUploads[ownerKey]) {
        if (state.pendingIconUploads[ownerKey].previewUrl) {
          try {
            URL.revokeObjectURL(state.pendingIconUploads[ownerKey].previewUrl)
          } catch (error) {
            console.warn("Failed to revoke icon blob URL:", error)
          }
        }
        delete state.pendingIconUploads[ownerKey]
      }
    },
    clearPendingIconUploads: (state) => {
      revokeAllPendingIconUrls(state.pendingIconUploads)
      state.pendingIconUploads = {}
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch draft
      .addCase(fetchDraft.pending, (state) => {
        state.isFetchingDraft = true
        state.error = null
      })
      .addCase(fetchDraft.fulfilled, (state, action) => {
        state.isFetchingDraft = false
        state.activeEnv = action.payload.env ?? state.activeEnv
        const draft = cloneConfigData(action.payload.draft)
        const templateKey = action.payload.templateKey ?? DEFAULT_TEMPLATE_KEY
        state.templateKey = templateKey
        if (draft && !draft.globalSettings) {
          draft.globalSettings = cloneConfigData(getTemplateConfig(templateKey).defaultGlobalSettings)
        }
        if (draft) {
          migrateComponentNames(draft, templateKey)
          migrateLocalizedFields(draft)
          migrateMiddleBannerStructure(draft)
        }
        state.draft = draft
        state.isDirty = false
        state.selectedPageId = draft?.pages?.[0]?.id || null
        state.selectedGlobalItemId = null
        state.draftContentHash = action.payload.draftContentHash || null
        state.publishedContentHash = action.payload.publishedContentHash || null
        state.deletedBannerImages = []
        // Очищаем blob URLs перед сбросом pending uploads
        resetPendingUploadsState(state)
      })
      .addCase(fetchDraft.rejected, (state, action) => {
        state.isFetchingDraft = false
        state.error = action.payload
      })
      // Save draft
      .addCase(saveDraft.pending, (state) => {
        state.isSaving = true
        state.error = null
      })
      .addCase(saveDraft.fulfilled, (state, action) => {
        state.isSaving = false
        state.isDirty = false
        state.lastSaved = new Date().toISOString()
        state.draftContentHash = action.payload.draftContentHash || state.draftContentHash
        state.deletedBannerImages = []

        // Обновляем draft с новыми imageUrl из response (после загрузки на R2)
        if (action.payload.draft) {
          state.draft = action.payload.draft
        }

        // Очищаем blob URLs перед сбросом pending uploads
        resetPendingUploadsState(state)
      })
      .addCase(saveDraft.rejected, (state, action) => {
        state.isSaving = false
        state.error = action.payload
      })
      // Publish
      .addCase(publishConfig.pending, (state) => {
        state.isPublishing = true
        state.error = null
      })
      .addCase(publishConfig.fulfilled, (state, action) => {
        state.isPublishing = false
        state.isDirty = false
        const newPublishedHash = action.payload.publishedContentHash || state.draftContentHash
        state.publishedContentHash = newPublishedHash
        state.draftContentHash = newPublishedHash

        // Обновляем draft с новыми imageUrl после публикации
        if (action.payload.draft) {
          state.draft = action.payload.draft
        }

        // Очищаем blob URLs перед сбросом pending uploads
        resetPendingUploadsState(state)
      })
      .addCase(publishConfig.rejected, (state, action) => {
        state.isPublishing = false
        state.error = action.payload
      })
  },
})

export const {
  selectPage,
  selectComponent,
  selectGlobalItem,
  setActiveEnv,
  updatePage,
  updateComponent,
  updateComponentSettings,
  updateGlobalItemSettings,
  addPage,
  removePage,
  addComponent,
  removeComponent,
  reorderComponents,
  clearBuilder,
  clearBuilderError,
  addDeletedBannerImage,
  clearDeletedBannerImages,
  addPendingBannerUpload,
  removePendingBannerUpload,
  clearPendingBannerUploads,
  addPendingIconUpload,
  removePendingIconUpload,
  clearPendingIconUploads,
} = builderSlice.actions

export const selectDraft = (state) => state.builder.draft
export const selectActiveEnv = (state) => state.builder.activeEnv
export const selectIsFetchingDraft = (state) => state.builder.isFetchingDraft
export const selectSelectedPageId = (state) => state.builder.selectedPageId
export const selectSelectedComponentId = (state) => state.builder.selectedComponentId
export const selectSelectedGlobalItemId = (state) => state.builder.selectedGlobalItemId
export const selectGlobalSettings = (state) => state.builder.draft?.globalSettings || null
export const selectIsDirty = (state) => state.builder.isDirty
export const selectIsSaving = (state) => state.builder.isSaving
export const selectIsPublishing = (state) => state.builder.isPublishing
export const selectBuilderError = (state) => state.builder.error
export const selectLastSaved = (state) => state.builder.lastSaved

// Derived selectors
export const selectSelectedPage = (state) => {
  const { draft, selectedPageId } = state.builder
  if (!draft || !selectedPageId) return null
  return draft.pages.find((p) => p.id === selectedPageId) || null
}

export const selectSelectedComponent = (state) => {
  const { draft, selectedPageId, selectedComponentId } = state.builder
  if (!draft || !selectedPageId || !selectedComponentId) return null
  const page = draft.pages.find((p) => p.id === selectedPageId)
  if (!page) return null
  return page.components.find((c) => c.id === selectedComponentId) || null
}

export const selectSelectedGlobalItem = (state) => {
  const { draft, selectedGlobalItemId } = state.builder
  if (!draft || !selectedGlobalItemId || !draft.globalSettings) return null
  return draft.globalSettings[selectedGlobalItemId] || null
}

export const selectHasUnpublishedChanges = (state) => {
  const { draftContentHash, publishedContentHash, isDirty } = state.builder
  // Если есть несохранённые изменения - точно есть что публиковать
  if (isDirty) return true
  // Если нет хэша draft - данные ещё не загружены
  if (!draftContentHash) return false
  // Если нет опубликованной версии - есть что публиковать
  if (!publishedContentHash) return true
  // Сравниваем хэши
  return draftContentHash !== publishedContentHash
}

export default builderSlice.reducer
